import { Badge, Button, Center, Divider, List, Paper, Stack, Text, ThemeIcon, Title } from '@mantine/core';
import { IconCalendarClock, IconCheck } from '@tabler/icons-react';
import { useEffect, useState } from 'react';

const TICK_MS = 30_000;

function formatCountdown(ms: number): string {
  const totalMinutes = Math.max(1, Math.ceil(ms / 60_000));
  const days = Math.floor(totalMinutes / 1_440);
  const hours = Math.floor((totalMinutes % 1_440) / 60);
  const minutes = totalMinutes % 60;
  if (days > 0) return hours > 0 ? `${days} d ${hours} h` : `${days} d`;
  if (hours > 0) return minutes > 0 ? `${hours} h ${minutes} min` : `${hours} h`;
  return `${minutes} min`;
}

/**
 * Shown when a client opens their session before joining is allowed. Explains when it opens and
 * calls `onOpen` once that time passes, so a page left open unlocks without a refresh.
 */
export function JoinNotOpenScreen({
  startIso,
  opensMinutesBefore,
  onOpen,
  onBack,
}: {
  startIso: string;
  opensMinutesBefore: number;
  onOpen?: () => void;
  onBack?: () => void;
}): JSX.Element {
  const [now, setNow] = useState(() => Date.now());
  const startMs = Date.parse(startIso);
  const opensMs = startMs - opensMinutesBefore * 60_000;
  const untilOpenMs = opensMs - now;

  useEffect(() => {
    if (untilOpenMs <= 0) {
      onOpen?.();
      return undefined;
    }
    const id = setTimeout(() => setNow(Date.now()), Math.min(TICK_MS, untilOpenMs + 1_000));
    return () => clearTimeout(id);
  }, [now, onOpen, untilOpenMs]);

  const start = new Date(startMs);
  const dateLabel = start.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' });
  const timeLabel = start.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit', timeZoneName: 'short' });
  const opensLabel = new Date(opensMs).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });

  return (
    <Center mih="calc(100dvh - 120px)" p="md">
      <Paper withBorder radius="lg" p="xl" maw={480} w="100%" shadow="sm">
        <Stack align="center" gap="md">
          <ThemeIcon size={56} radius="xl" variant="light">
            <IconCalendarClock size={30} />
          </ThemeIcon>
          <Title order={3} ta="center">
            Your session isn&apos;t open yet
          </Title>
          <Stack gap={2} align="center">
            <Text fw={600} ta="center">
              {dateLabel}
            </Text>
            <Text c="dimmed" ta="center">
              Appointment at {timeLabel}
            </Text>
          </Stack>
          <Badge size="lg" variant="light" radius="sm">
            Opens in {formatCountdown(untilOpenMs)}
          </Badge>
          <Text size="sm" ta="center">
            You can join the waiting room from {opensLabel}, {opensMinutesBefore} minutes before your
            appointment starts. Keep this page open and it will unlock on its own.
          </Text>
          <Divider w="100%" />
          <Stack gap="xs" w="100%">
            <Text size="sm" fw={600}>
              Before you join
            </Text>
            <List
              size="sm"
              spacing={6}
              icon={
                <ThemeIcon size={18} radius="xl" variant="light" color="teal">
                  <IconCheck size={12} />
                </ThemeIcon>
              }
            >
              <List.Item>Find a quiet, private space where you can talk freely.</List.Item>
              <List.Item>Check that your camera and microphone work.</List.Item>
              <List.Item>Use a stable internet connection, and plug in if your battery is low.</List.Item>
            </List>
          </Stack>
          {onBack ? (
            <Button variant="default" onClick={onBack} mt="xs">
              Back to portal
            </Button>
          ) : null}
        </Stack>
      </Paper>
    </Center>
  );
}
